/**
 * ファームウェアバージョン管理ページ
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { firmwareApi } from '../services/api';
import { Icon } from '../components/icons';
import { Button, Card, Alert, Input, Select, Modal, Badge } from '../components/ui';

const STATUS_LABELS = {
  draft: { label: '下書き', color: 'bg-slate-100 text-slate-600' },
  testing: { label: 'テスト中', color: 'bg-yellow-100 text-yellow-700' },
  released: { label: 'リリース済', color: 'bg-green-100 text-green-700' },
  deprecated: { label: '非推奨', color: 'bg-red-100 text-red-700' },
};

export default function FirmwareVersions() {
  const [firmware, setFirmware] = useState([]);
  const [selectedFw, setSelectedFw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 新規登録
  const [showCreate, setShowCreate] = useState(false);
  const [newVersion, setNewVersion] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newReleaseNotes, setNewReleaseNotes] = useState('');
  const [newUpdateType, setNewUpdateType] = useState('ota-update');
  const [creating, setCreating] = useState(false);
  const versionRef = useRef(null);

  // 編集
  const [showEdit, setShowEdit] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  // アップロード
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const fileInputRef = useRef(null);

  // データ取得
  const fetchFirmware = useCallback(async () => {
    setLoading(true);
    try {
      const data = await firmwareApi.list();
      setFirmware(data.firmware || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFirmware();
  }, [fetchFirmware]);

  // 詳細選択
  const selectFirmware = async (fw) => {
    try {
      const detail = await firmwareApi.get(fw.version);
      setSelectedFw(detail);
    } catch (err) {
      console.error('Firmware detail error:', err);
      setSelectedFw(fw);
    }
  };

  // 新規登録
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newVersion.trim()) return;

    if (!/^[\d]+\.[\d]+\.[\d]+(-[\w]+)?$/.test(newVersion.trim())) {
      alert('バージョンは X.Y.Z 形式で入力してください（例: 1.0.0, 2.1.0-beta）');
      return;
    }

    setCreating(true);
    try {
      await firmwareApi.create({
        version: newVersion.trim(),
        description: newDescription.trim(),
        releaseNotes: newReleaseNotes.trim(),
        updateType: newUpdateType,
      });
      setNewVersion('');
      setNewDescription('');
      setNewReleaseNotes('');
      setNewUpdateType('ota-update');
      setShowCreate(false);
      await fetchFirmware();
    } catch (err) {
      alert('登録エラー: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  // 削除
  const handleDelete = async (version) => {
    if (!window.confirm(`バージョン ${version} を削除しますか？`)) return;
    try {
      await firmwareApi.delete(version);
      if (selectedFw?.version === version) {
        setSelectedFw(null);
      }
      await fetchFirmware();
    } catch (err) {
      alert('削除エラー: ' + err.message);
    }
  };

  // 編集開始
  const startEdit = () => {
    setEditData({
      description: selectedFw.description || '',
      releaseNotes: selectedFw.releaseNotes || '',
      status: selectedFw.status || 'draft',
    });
    setShowEdit(true);
  };

  // 編集保存
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await firmwareApi.update(selectedFw.version, editData);
      setSelectedFw(updated);
      setShowEdit(false);
      await fetchFirmware();
    } catch (err) {
      alert('更新エラー: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // ファイルアップロード
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress('アップロード準備中...');
    try {
      setUploadProgress('アップロード中...');
      await firmwareApi.uploadFile(selectedFw.version, file);
      setUploadProgress('完了');

      // 詳細を再取得
      const detail = await firmwareApi.get(selectedFw.version);
      setSelectedFw(detail);
      await fetchFirmware();

      setTimeout(() => setUploadProgress(null), 2000);
    } catch (err) {
      alert('アップロードエラー: ' + err.message);
      setUploadProgress(null);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (showCreate) setTimeout(() => versionRef.current?.focus(), 50);
  }, [showCreate]);

  const formatDate = (isoStr) => {
    if (!isoStr) return '-';
    return new Date(isoStr).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-500">ファームウェアバージョンの登録・管理</p>

      {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}

      <div className="grid md:grid-cols-2 gap-6">
        {/* バージョン一覧 */}
        <div className="space-y-3">
          <Card title="バージョン一覧" padding="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : firmware.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                ファームウェアなし
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {firmware.map((fw) => (
                  <div
                    key={fw.version}
                    className={`
                      flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors
                      ${selectedFw?.version === fw.version
                        ? 'bg-primary-50'
                        : 'hover:bg-gray-50'
                      }
                    `}
                    onClick={() => selectFirmware(fw)}
                  >
                    <Icon name="download" className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{fw.version}</span>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_LABELS[fw.status]?.color || 'bg-slate-100'}`}>
                          {STATUS_LABELS[fw.status]?.label || fw.status}
                        </span>
                      </div>
                      {fw.description && (
                        <div className="text-xs text-slate-500 truncate">
                          {fw.description}
                        </div>
                      )}
                    </div>
                    {fw.status !== 'released' && (
                      <button
                        className="p-1.5 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        onClick={(e) => { e.stopPropagation(); handleDelete(fw.version); }}
                        title="削除"
                      >
                        <Icon name="trash" className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
          <Button variant="secondary" onClick={() => setShowCreate(true)}>
            新規バージョン登録
          </Button>
        </div>

        {/* 詳細 */}
        <Card padding="p-0">
          {selectedFw ? (
            <div>
              <div className="px-4 py-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon name="download" className="w-5 h-5 text-primary-500" />
                    <h3 className="font-semibold text-slate-900">v{selectedFw.version}</h3>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_LABELS[selectedFw.status]?.color || 'bg-slate-100'}`}>
                      {STATUS_LABELS[selectedFw.status]?.label || selectedFw.status}
                    </span>
                  </div>
                  <Button size="sm" variant="secondary" onClick={startEdit}>
                    <Icon name="edit" className="w-4 h-4" />
                    編集
                  </Button>
                </div>
                {selectedFw.description && (
                  <p className="text-sm text-slate-600 mt-2">{selectedFw.description}</p>
                )}
              </div>

              <div className="px-4 py-4 space-y-4">
                {/* ファイル情報 */}
                <div>
                  <h4 className="text-xs font-medium text-slate-500 uppercase mb-2">ファイル</h4>
                  <div className="bg-slate-50 rounded-lg p-3">
                    {selectedFw.uploadedAt ? (
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">S3 Key:</span>
                          <span className="text-slate-700 font-mono text-xs">{selectedFw.s3Key}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">サイズ:</span>
                          <span className="text-slate-700">{formatSize(selectedFw.fileSize)}</span>
                        </div>
                        {selectedFw.checksum && (
                          <div className="flex justify-between items-start">
                            <span className="text-slate-500 flex-shrink-0">
                              {selectedFw.checksumAlgorithm || 'SHA-256'}:
                            </span>
                            <span className="text-slate-700 font-mono text-xs break-all ml-2">
                              {selectedFw.checksum}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-slate-500">アップロード:</span>
                          <span className="text-slate-700">{formatDate(selectedFw.uploadedAt)}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-2">
                        <p className="text-sm text-slate-500 mb-2">ファイル未アップロード</p>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".bin,.zip,.tar.gz"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => fileInputRef.current?.click()}
                          loading={uploading}
                        >
                          <Icon name="upload" className="w-4 h-4" />
                          アップロード
                        </Button>
                        {uploadProgress && (
                          <p className="text-xs text-primary-500 mt-2">{uploadProgress}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* リリースノート */}
                {selectedFw.releaseNotes && (
                  <div>
                    <h4 className="text-xs font-medium text-slate-500 uppercase mb-2">リリースノート</h4>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <pre className="text-sm text-slate-700 whitespace-pre-wrap">{selectedFw.releaseNotes}</pre>
                    </div>
                  </div>
                )}

                {/* メタ情報 */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">作成日時:</span>
                    <p className="text-slate-700">{formatDate(selectedFw.createdAt)}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">更新日時:</span>
                    <p className="text-slate-700">{formatDate(selectedFw.updatedAt)}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Icon name="chevronLeft" className="w-8 h-8 mb-2" />
              <p>バージョンを選択してください</p>
            </div>
          )}
        </Card>
      </div>

      {/* 新規登録モーダル */}
      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="新規ファームウェア登録"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>
              キャンセル
            </Button>
            <Button onClick={handleCreate} loading={creating}>
              登録
            </Button>
          </>
        }
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <Input
              ref={versionRef}
              label="バージョン"
              required
              value={newVersion}
              onChange={(e) => setNewVersion(e.target.value)}
              placeholder="例: 1.0.0, 2.1.0-beta"
            />
            <p className="text-xs text-slate-500 mt-1">
              X.Y.Z 形式（例: 1.0.0, 2.1.0-beta）
            </p>
          </div>
          <div>
            <Input
              label="説明"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="例: 初回リリース版"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              更新方式
            </label>
            <select
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              value={newUpdateType}
              onChange={(e) => setNewUpdateType(e.target.value)}
            >
              <option value="ota-update">Agent更新（ota-agent自己更新）</option>
              <option value="rauc-update">RAUCバンドル（OS全体・A/Bパーティション更新）</option>
            </select>
            <p className="text-xs text-slate-500 mt-1">
              RAUCバンドルは .raucb ファイルをアップロードしてください。デバイスは適用後に自動再起動します。
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              リリースノート
            </label>
            <textarea
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              rows={4}
              value={newReleaseNotes}
              onChange={(e) => setNewReleaseNotes(e.target.value)}
              placeholder="変更点など..."
            />
          </div>
        </form>
      </Modal>

      {/* 編集モーダル */}
      <Modal
        isOpen={showEdit}
        onClose={() => setShowEdit(false)}
        title={`v${selectedFw?.version} 編集`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowEdit(false)}>
              キャンセル
            </Button>
            <Button onClick={handleSaveEdit} loading={saving}>
              保存
            </Button>
          </>
        }
      >
        <form onSubmit={handleSaveEdit} className="space-y-4">
          <div>
            <Input
              label="説明"
              value={editData.description || ''}
              onChange={(e) => setEditData({ ...editData, description: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              リリースノート
            </label>
            <textarea
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              rows={4}
              value={editData.releaseNotes || ''}
              onChange={(e) => setEditData({ ...editData, releaseNotes: e.target.value })}
            />
          </div>
          <div>
            <Select
              label="ステータス"
              value={editData.status || 'draft'}
              onChange={(e) => setEditData({ ...editData, status: e.target.value })}
              options={[
                { value: 'draft', label: '下書き' },
                { value: 'testing', label: 'テスト中' },
                { value: 'released', label: 'リリース済' },
                { value: 'deprecated', label: '非推奨' },
              ]}
            />
            <p className="text-xs text-slate-500 mt-1">
              リリース済のファームウェアは削除できません
            </p>
          </div>
        </form>
      </Modal>
    </div>
  );
}
